import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, ROLE_LABEL } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, ShieldCheck, ShieldStar, UserGear, Eye, Key } from "@phosphor-icons/react";

const BLANK = { username: "", name: "", email: "", role: "staff", password: "" };

const ROLE_INFO = [
  { value: "admin", label: "Admin", icon: ShieldStar,
    desc: "Full system access, user management, salary amendment, and deletion of records." },
  { value: "director", label: "Director", icon: ShieldStar,
    desc: "Executive leadership: sees all revenue & cost, approves POs > K25,000." },
  { value: "finance", label: "Finance", icon: ShieldCheck,
    desc: "Payroll, salaries, PAYE, gratuity — full HR + salary read access, no salary edit." },
  { value: "hr", label: "HR", icon: ShieldCheck,
    desc: "People operations: employees, PPE, documents, payroll register — no salary numbers." },
  { value: "manager", label: "Manager", icon: ShieldCheck,
    desc: "Approve POs (< K25k), requests, manage trucks/machines/projects. No HR access." },
  { value: "staff", label: "Staff", icon: UserGear,
    desc: "Daily operators: create POs/quotations/orders/requests, log production, adjust stock." },
  { value: "viewer", label: "Viewer", icon: Eye,
    desc: "Read-only access to operations dashboards. No money or HR visibility." },
];

export default function UsersPage() {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  async function load() {
    try { const { data } = await api.get("/users"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setEditOpen(true); }
  function openEdit(u) {
    setEditing(u);
    setForm({ username: u.username || "", name: u.name, email: u.email || "", role: u.role, password: "" });
    setEditOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.email) payload.email = form.email;
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", {
          username: form.username, password: form.password,
          name: form.name, role: form.role,
          email: form.email || null,
        });
      }
      toast.success("Saved"); setEditOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(u) {
    if (!window.confirm(`Delete user "${u.username || u.email}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  const [resetResult, setResetResult] = useState(null);

  async function resetPassword(u) {
    if (!window.confirm(
      `Reset password for "${u.username || u.email}"?\n\n`
      + "They will be forced to choose a new password on next login. "
      + "The temporary password will be shown to you to share — copy it now.")) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setResetResult(data);
      if (data.emailed) toast.success(`Reset — temp password also emailed to ${u.email}`);
      else toast.success("Password reset — copy the temporary password below");
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  const canReset = me?.role === "admin" || me?.role === "hr" || me?.role === "luroma_provider";

  return (
    <div data-testid="users-page">
      <PageHeader
        overline="Module / Admin"
        title="Users & Access"
        subtitle="Create accounts and grant role-based access. Only Admins can manage users."
        actions={
          <BtnPrimary onClick={openCreate} data-testid="user-create">
            <Plus size={14} weight="bold" className="inline mr-1.5" /> Add User
          </BtnPrimary>
        }
      />

      {/* Role overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {ROLE_INFO.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.value} className="phizo-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={18} weight="bold" className="text-[#002FA7]" />
                <div className="font-heading font-bold text-base">{r.label}</div>
              </div>
              <div className="text-xs text-[#52525b] leading-relaxed">{r.desc}</div>
            </div>
          );
        })}
      </div>

      <div className="phizo-card">
        {rows.length === 0 ? <Empty /> : (
          <table className="phizo-table">
            <thead><tr><th>Username</th><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.username || u.email}`}
                  onClick={() => nav(`/users/${u.id}`)}
                  className="cursor-pointer hover:bg-[#f4f4f5]"
                >
                  <td className="mono font-semibold">{u.username || "—"}</td>
                  <td className="font-medium">{u.name}</td>
                  <td className="mono text-xs text-[#52525b]">{u.email || "—"}</td>
                  <td>
                    <span className="phizo-status"
                      style={{ background: "#002FA7", color: "#fff", borderColor: "#002FA7" }}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="text-sm text-[#52525b]">{fmtDate(u.created_at)}</td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <BtnGhost onClick={() => openEdit(u)} data-testid={`user-edit-${u.username}`}>
                      <PencilSimple size={12} weight="bold" />
                    </BtnGhost>
                    {canReset && u.id !== me.id && (
                      <BtnGhost
                        onClick={() => resetPassword(u)}
                        data-testid={`user-reset-${u.username}`}
                        title="Reset password"
                      >
                        <Key size={12} weight="bold" />
                      </BtnGhost>
                    )}
                    {u.id !== me.id && (
                      <BtnGhost onClick={() => remove(u)} className="text-[#FF2A2A]"
                        data-testid={`user-delete-${u.username}`}>
                        <Trash size={12} weight="bold" />
                      </BtnGhost>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Temporary password issued">
        {resetResult && (
          <div className="space-y-4">
            <p className="text-sm text-[#52525b]">
              Share this temporary password with <b>{resetResult.username}</b> over a secure channel.
              They will be prompted to set a new password on their next sign-in.
            </p>
            <div className="border border-[#e4e4e7] bg-[#fafafa] p-4 flex items-center justify-between gap-3">
              <div className="mono text-xl font-black tracking-widest select-all" data-testid="reset-temp-password">
                {resetResult.temp_password}
              </div>
              <BtnSecondary
                onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success("Copied to clipboard"); }}
                data-testid="reset-copy-btn"
              >
                Copy
              </BtnSecondary>
            </div>
            <div className="text-xs text-[#71717a]">
              {resetResult.emailed
                ? "✉ Also emailed to the user's registered address."
                : "This password is shown ONCE — copy it now. It will not appear again."}
            </div>
            <div className="flex justify-end pt-3 border-t border-[#e4e4e7]">
              <BtnPrimary onClick={() => setResetResult(null)}>Done</BtnPrimary>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}
        title={editing ? `Edit ${editing.username || editing.email}` : "Create User"} wide>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username" hint="Used to sign in (e.g. jbanda)">
              <Input required minLength={2} disabled={!!editing}
                value={form.username} data-testid="u-username"
                onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </Field>
            <Field label="Full Name">
              <Input required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" />
            </Field>
          </div>
          <Field label="Email (optional, for receipts & resets)">
            <Input type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" />
          </Field>
          <Field label="Role / Access Level">
            <div className="grid grid-cols-2 gap-2">
              {ROLE_INFO.map((r) => {
                const Icon = r.icon;
                const selected = form.role === r.value;
                return (
                  <button
                    key={r.value} type="button"
                    data-testid={`u-role-${r.value}`}
                    onClick={() => setForm({ ...form, role: r.value })}
                    className={`p-3 text-left border transition-colors ${
                      selected
                        ? "border-[#002FA7] bg-[#002FA7] text-white"
                        : "border-[#e4e4e7] bg-white hover:border-[#002FA7]"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={16} weight="bold" />
                      <span className="font-bold text-sm">{r.label}</span>
                    </div>
                    <div className={`text-[10px] leading-snug ${selected ? "text-white/80" : "text-[#52525b]"}`}>
                      {r.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label={editing ? "New Password (leave blank to keep)" : "Password"}>
            <Input type="password" required={!editing} minLength={6}
              value={form.password} data-testid="u-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setEditOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="u-save">{editing ? "Save" : "Create User"}</BtnPrimary>
          </div>
        </form>
      </Modal>
    </div>
  );
}
